export const FUEL_TYPE_OPTIONS = [
    { value: 'ron95', label: 'RON 95' },
    { value: 'ron97', label: 'RON 97' },
    { value: 'diesel', label: 'Diesel (Peninsular)' },
    { value: 'diesel_eastmsia', label: 'Diesel (East Malaysia)' },
    { value: 'ron95_budi95', label: 'RON 95 BUDI95' },
    { value: 'ron95_skps', label: 'RON 95 SKPS' },
    { value: 'diesel_skds', label: 'Diesel (SKDS)' },
    { value: 'diesel_budi', label: 'Diesel (BUDI)' },
];

const FUEL_TYPE_LABEL_BY_VALUE = FUEL_TYPE_OPTIONS.reduce((accumulator, { value, label }) => {
    accumulator[value] = label;
    return accumulator;
}, {});

const FUEL_TYPE_VALUE_SET = new Set(FUEL_TYPE_OPTIONS.map(({ value }) => value));

export const getFuelTypeLabel = (value) => FUEL_TYPE_LABEL_BY_VALUE[value] || value || '—';

export const isKnownFuelType = (value) => FUEL_TYPE_VALUE_SET.has(value);

const isFinitePrice = (value) => {
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric);
};

const isCurrentRow = (row) => {
    if (typeof row?.isCurrent === 'boolean') return row.isCurrent;
    return row?.effectiveTo === null || row?.effectiveTo === undefined || row?.effectiveTo === '';
};

const buildRowId = (row, index) => {
    const parts = [
        row?.id,
        row?.fuelType,
        row?.effectiveFrom,
        row?.effectiveTo || 'current',
        index,
    ].filter(Boolean);
    return parts.join('-');
};

/** Pull the `data` array out of either an axios response or a bare payload. */
export const extractFuelPriceData = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    const payload = response.data ?? response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.fuelPrices)) return payload.fuelPrices;
    return [];
};

/** Normalise API rows into the shape consumed by the table. */
export const buildFuelPriceRows = (apiRows) => {
    if (!Array.isArray(apiRows) || apiRows.length === 0) return [];

    return apiRows
        .filter((row) => row && isFinitePrice(row.price))
        .map((row, index) => {
            const current = isCurrentRow(row);
            return {
                id: buildRowId(row, index),
                fuelType: row.fuelType,
                price: Number(row.price),
                effectiveFrom: row.effectiveFrom || null,
                effectiveTo: current ? null : (row.effectiveTo || null),
                isCurrent: current,
                source: row.source || '—',
                seriesType: row.seriesType || row.series_type || null,
                lastSyncedAt: row.lastSyncedAt || null,
                syncRevision: row.syncRevision || null,
                status: current ? 'ACTIVE' : 'ARCHIVED',
            };
        })
        .sort((a, b) => {
            const aTs = a.effectiveFrom ? new Date(a.effectiveFrom).getTime() : 0;
            const bTs = b.effectiveFrom ? new Date(b.effectiveFrom).getTime() : 0;
            return bTs - aTs;
        });
};

export const hasFuelFilters = ({ fuelType, dateFrom, dateTo } = {}) => (
    (Boolean(fuelType) && fuelType !== 'ALL')
    || Boolean(dateFrom)
    || Boolean(dateTo)
);

const parseRowDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};

/**
 * Defensive client-side filter — the API already honours fuelType / date
 * range, but we double-check so stale cache entries don't leak rows the
 * user has filtered out.
 */
export const filterFuelPriceRows = (rows, { fuelType, dateFrom, dateTo } = {}) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const fromTs = parseRowDate(dateFrom);
    const toTs = dateTo ? (() => {
        const end = new Date(dateTo);
        if (Number.isNaN(end.getTime())) return null;
        end.setHours(23, 59, 59, 999);
        return end.getTime();
    })() : null;

    return rows.filter((row) => {
        if (fuelType && fuelType !== 'ALL' && row.fuelType !== fuelType) return false;
        const rowTs = parseRowDate(row.effectiveFrom);
        if (rowTs === null) return false;
        if (fromTs !== null && rowTs < fromTs) return false;
        if (toTs !== null && rowTs > toTs) return false;
        return true;
    });
};

export const getDisplayedFuelRows = (rows, filters) => (
    hasFuelFilters(filters)
        ? filterFuelPriceRows(rows, filters)
        : rows
);

/** Active price per fuel type (current row only) — used by the read-only view. */
export const buildActiveFuelPriceMap = (rows) => {
    const details = buildActiveFuelDetailsMap(rows);
    return Object.keys(details).reduce((accumulator, fuelType) => {
        accumulator[fuelType] = details[fuelType].price;
        return accumulator;
    }, {});
};

/** Active current row per fuel type — price + Mongo `source` for read-only tooltips. */
export const buildActiveFuelDetailsMap = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return {};
    return rows.reduce((accumulator, row) => {
        if (!row.isCurrent) return accumulator;
        if (accumulator[row.fuelType] !== undefined) return accumulator;
        accumulator[row.fuelType] = {
            price: row.price,
            source: row.source || null,
        };
        return accumulator;
    }, {});
};
